/**
 * Login Component
 * 
 * Description: This component handles the admin login process. It includes form fields for username and password, and functionality to toggle password visibility. 
 * The form submits the login details and displays an error message if any fields are empty.
 * 
 * File Name: Login.tsx
 * Date: 2024-07-22
 */

import React, { useState, FormEvent, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import InputFeild from "src/Components/InputFeild";
import CircleButton from "src/Components/GlobalComponents/CircleButton";
import httpRequest from "../../helpers/httpRequest";
import { toast } from "react-toastify";
import { twoFactorKey } from "../../lib/constans";
import moment from "moment";
import { getToken, removeToken } from "src/helpers/helperFunctions";
import { getIndicators, removeAllIndicators } from "src/Store/redDots";
import { useDispatch } from "react-redux";
import { removeAuth, setUserDetails, setAuth } from "src/Store/reducer";

const Login: React.FC = () => {
  const navigate = useNavigate();
  // State to toggle password visibility
  const [showPassword, setShowPassword] = useState<boolean>(false);
  // State for username, password, and loader
  const [userName, setUserName] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [keepMeLogedIn, setKeepMeLogedIn] = useState<boolean>(false);
  const [isLoader, setIsLoader] = useState<boolean>(false);
  const dispatch = useDispatch();

  // Define the response type based on the expected structure
  interface ILoginResponse {
    otpExpire?: string;
    message?: string;
    [key: string]: any;  // Allow for any additional properties
  }

  // API_CALL: API call for login admin
  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!userName || !password) {
      toast("All fields are required!", { type: 'error' });
      return;
    }
    setIsLoader(true);
    const { res, err } = await httpRequest<ILoginResponse>({ method: "post", path: "/auth/login", params: { userName, password, keepMeLogedIn } });
    if (res) {
      let { message = "" , token } = res;
      // let { message = "" } = res;
      toast(message || "Success!", { type: 'success' });
      // Store relevant information in sessionStorage
      let obj = { userName, keepMeLogedIn, otpExpire: moment().add(2, 'minutes').toISOString() };
      sessionStorage.setItem(twoFactorKey, JSON.stringify(obj));
      // Redirect to OTP page
      navigate("/admin/otp");
      // Reset form fields
      setUserName("");
      setPassword("");
      setKeepMeLogedIn(false);
    } else {
      toast(err, { type: 'error' });
    }
    setIsLoader(false);
  };

  return (
    <div className="customer-login-main">
      <div className="container-1600">
        <div className="customer-login-main-inner">
          <div>
            <h1 className="font-Poppins-SemiBold font-size-32px theme-color-green leading-none text-center lg-main-head">Welcome!</h1>
            <p className="font-size-16px theme-color-green font-Poppins-Regular">To login please enter your username</p>
          </div>
          <form style={{ width: '100%' }} onSubmit={handleSubmit}>
            <div className="customer-login-main-inner-main">
              <div>
                <InputFeild
                  id="userName"
                  label='Username'
                  value={userName}
                  onChange={setUserName}
                />
              </div>
              <div>
                <InputFeild
                  id="password"
                  type="password"
                  label='Password'
                  value={password}
                  onChange={setPassword}
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  className="input-checkbox"
                  type="checkbox"
                  checked={keepMeLogedIn}
                  onChange={() => setKeepMeLogedIn(prev => !prev)}
                  id="keep-me-logged-in"
                />
                <label htmlFor="keep-me-logged-in" className="font-size-13px font-Poppins-Medium text-white inline-block cursor-pointer">
                  Keep me logged in
                </label>
              </div>
              <div className="login-btn-home-banner-parent flex items-center justify-center">
                <CircleButton
                  id="submit_btn"
                  ButtonText={'Login'}
                  loader={isLoader}
                  type={"submit"}
                />
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;